import React, { useEffect, useMemo, useRef, useState } from "react"

import { useBackToClose } from "@/lib/use-back-to-close"
import NewSocialSuggestFeeds, {
  type SuggestFeed,
} from "@/shadcn/components/social/new-social-suggest-feeds"
import {
  collectSuggestedFeeds,
  feedCadence,
  isSocialOnRoute,
  pickMostActive,
  routeHasMasterTweeter,
  selectedRouteIDs,
} from "app/utility/SuggestFeeds.js"
import { useRecordEvent } from "@/lib/use-record-event"

// Production wiring for new-social-suggest-feeds. Takes a social id, derives the
// feeds it could be added to from the routes already in the store (see
// app/utility/SuggestFeeds.js — a route's inputs are its feeds, minus the routes
// this account is itself the source of), and owns the selection / search /
// pagination / step state the picker renders from.
//
// Confirming adds the social as an output of each selected feed's route: one
// output per route however many of its inputs were ticked, since a route
// publishes to a social once. "Share most recent item" is the output's
// postRecent flag.
//
// A twitter/X account is created as the route's master tweeter when that route
// has no master yet, since the server would otherwise default it to a retweeter
// with nothing to retweet (see routeHasMasterTweeter).
//
// Confirming keeps the dialog open until every create has settled: the outputs go
// out as separate requests and the server can reject any one of them on its own
// (twitter output caps, plan limits, an output that would publish back into its
// own source). A create is settled once the store shows the social on that route
// — the OUTPUT_CREATE reducer pushes it — or once an OUTPUT_CREATE api error
// arrives. Failures are reported in place so the user can retry, and onComplete
// only fires once nothing is still in flight.
//
// Not carried over from the Figma frame: Sort & filter has no behaviour behind
// it yet, so no handler is passed and the picker hides the button.

const PAGE_SIZE = 6

// how many numbered pages sit before the ellipsis, per the Figma frame
const PAGE_WINDOW = 4

type NewSocialSuggestFeedsModalProps = {
  /** postAccount id the feeds would publish to. */
  socialID: number | string
  /** state.socials */
  socials: any
  /** state.automatedRoutes */
  automatedRoutes: any
  /** app.config.constants */
  constants: any
  createOutput: (output: any) => void
  /** getApiErrors(['OUTPUT_CREATE']) — the rejections this dialog reports. */
  apiErrors: any[]
  deleteApiError: (keys: string[]) => void
  closeAction: () => void
  /** Called with the route ids that gained the social, once every create succeeded. */
  onComplete?: (routeIDs: number[]) => void
}

// Why the candidate list came out empty. "every feed it can" rather than "every
// feed" because the feeds this account is the source of are never candidates.
const noCandidatesLabel = (connectedCount: number, loopCount: number) => {
  if (connectedCount) {
    return "This social already posts to every feed it can."
  }

  if (loopCount) {
    return "Your feeds all pull from this account, so it can't post to them."
  }

  return "You don't have any feeds to add this social to yet."
}

// The validation text the api returns for a rejected output ("Twitter outputs
// limited to 10.", a plan limit, ...), so the dialog can say what actually went
// wrong instead of closing as though it had worked.
const createErrorText = (errors: any[]) => {
  const messages: string[] = []

  for (const entry of errors) {
    const validation = (entry && entry.errors && entry.errors.validation) || {}

    for (const message of Object.values(validation)) {
      if (typeof message === "string" && !messages.includes(message)) {
        messages.push(message)
      }
    }
  }

  if (!messages.length) {
    return "Those feeds couldn't be added. Please try again."
  }

  return messages.join(" ")
}

const initials = (name: string) =>
  name
    .replace(/^@/, "")
    .split(/[\s_.-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?"

export default function NewSocialSuggestFeedsModal({
  socialID,
  socials,
  automatedRoutes,
  constants,
  createOutput,
  apiErrors,
  deleteApiError,
  closeAction,
  onComplete,
}: NewSocialSuggestFeedsModalProps) {
  const [step, setStep] = useState<"browse" | "confirm">("browse")
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [feedsExpanded, setFeedsExpanded] = useState(false)
  const [shareMostRecent, setShareMostRecent] = useState(true)
  const [saving, setSaving] = useState(false)
  // the routes whose creates are in flight, so the settle check knows what to wait on
  const [pendingRouteIDs, setPendingRouteIDs] = useState<number[]>([])
  const [error, setError] = useState("")

  const recordEvent = useRecordEvent()

  const paID = parseInt(String(socialID), 10)

  const social = useMemo(
    () =>
      (socials.list || []).find((entry: any) => parseInt(entry.id, 10) === paID),
    [socials.list, paID]
  )

  const externalID = social ? social.externalID : ""

  const { feeds, connectedCount, loopCount } = useMemo(
    () => collectSuggestedFeeds(automatedRoutes.list, paID, externalID),
    [automatedRoutes.list, paID, externalID]
  )

  const routeByID = useMemo(
    () =>
      new Map<number, any>(
        (automatedRoutes.list || []).map((route: any) => [
          parseInt(route.id, 10),
          route,
        ])
      ),
    [automatedRoutes.list]
  )

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase()

    if (!term) {
      return feeds
    }

    return feeds.filter(
      (feed: any) =>
        feed.name.toLowerCase().includes(term) ||
        feed.routeName.toLowerCase().includes(term)
    )
  }, [feeds, search])

  const { recommended, rest } = useMemo(() => pickMostActive(matches), [matches])

  // Seed the selection with the most active feeds, matching the design's
  // pre-checked recommendations. Deferred to an effect because the routes may
  // still be loading when the modal mounts.
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current || !feeds.length) {
      return
    }

    seeded.current = true
    setSelectedIds(pickMostActive(feeds).recommended.map((feed: any) => feed.id))
  }, [feeds])

  // a rejected create from anywhere else in the app would otherwise read as this
  // dialog's own failure the moment it saves
  useEffect(() => {
    deleteApiError(["OUTPUT_CREATE"])
  }, [deleteApiError])

  useEffect(() => {
    document.body.classList.add("modal-open")

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeAction()
      }
    }

    window.addEventListener("keyup", onKeyUp)

    return () => {
      document.body.classList.remove("modal-open")
      window.removeEventListener("keyup", onKeyUp)
    }
  }, [closeAction])

  // Back dismisses this the way it dismisses every legacy modal. Not part of the
  // effect above, which is keyed on `closeAction` and so re-runs — that would
  // rebuild the history entry each time.
  useBackToClose(closeAction)

  const totalPages = Math.max(1, Math.ceil(rest.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pageFeeds = rest.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  // a sliding window of page numbers, so a long feed list doesn't overflow the row
  const firstPage = Math.min(
    Math.max(1, currentPage - 1),
    Math.max(1, totalPages - PAGE_WINDOW + 1)
  )
  const pages = []
  for (let p = firstPage; p <= Math.min(totalPages, firstPage + PAGE_WINDOW - 1); ++p) {
    pages.push(p)
  }

  const toSuggestFeed = (feed: any): SuggestFeed => {
    const cadence = feedCadence(feed.inputVelocity)

    return {
      id: feed.id,
      name: feed.name,
      updatedAgo: feed.updatedAgo,
      cadence,
      // line 2 belongs to the update rate; the route name stands in only while a
      // feed has no velocity yet, and only when it isn't the feed's name again
      subtitle:
        !cadence && feed.routeName !== feed.name ? feed.routeName : undefined,
      logo: feed.icon ? (
        <img src={feed.icon} alt="" className="tw:size-full tw:object-cover" />
      ) : undefined,
    }
  }

  const selection = new Set(selectedIds)
  const chosen = feeds.filter((feed: any) => selection.has(feed.id))

  const toggleFeed = (id: string, checked: boolean) => {
    setSelectedIds((current) =>
      checked
        ? current.includes(id)
          ? current
          : [...current, id]
        : current.filter((entry) => entry !== id)
    )
  }

  const toggleFeeds = (ids: string[], checked: boolean) => {
    setSelectedIds((current) => {
      if (!checked) {
        return current.filter((entry) => !ids.includes(entry))
      }

      return [...current, ...ids.filter((id) => !current.includes(id))]
    })
  }

  const confirm = () => {
    const routeIDs = selectedRouteIDs(chosen)
    const twitterSvcID = parseInt(constants.POST_SVC_TWITTER, 10)
    const isTwitter = Boolean(social) && parseInt(social.psID, 10) === twitterSvcID

    // errors from a previous attempt are not this attempt's verdict
    deleteApiError(["OUTPUT_CREATE"])
    setError("")
    setPendingRouteIDs(routeIDs)
    setSaving(true)

    for (const rID of routeIDs) {
      const output: Record<string, any> = {
        rID,
        paID,
        activeFlag: true,
        postRecent: shareMostRecent ? 1 : 0,
      }

      // The master role is per route, so this is decided per output: the first
      // twitter account on a route leads and the rest retweet it.
      if (
        isTwitter &&
        !routeHasMasterTweeter(
          routeByID.get(rID),
          twitterSvcID,
          constants.OR_ROLE_NORMAL
        )
      ) {
        output.role = constants.OR_ROLE_NORMAL
      }

      recordEvent("Adding Social to Feeds with SFM")

      createOutput(output)
    }
  }

  // Settle the save: every pending route either shows the social (the create
  // landed) or is gone from the list, and every rejection is counted. A route
  // that has dropped out of the list has nothing left to wait for.
  useEffect(() => {
    if (!saving || !pendingRouteIDs.length) {
      return
    }

    const landed = pendingRouteIDs.filter((rID) => {
      const route = routeByID.get(rID)

      return !route || isSocialOnRoute(route, paID)
    }).length

    if (landed + apiErrors.length < pendingRouteIDs.length) {
      return
    }

    setSaving(false)

    if (apiErrors.length) {
      // the dialog stays on the confirm step so the user can retry; any feed
      // that did land has left the candidate list, so a retry only re-sends
      // what failed
      setError(createErrorText(apiErrors))
      setPendingRouteIDs([])

      return
    }

    onComplete?.(pendingRouteIDs)
    closeAction()
  }, [
    saving,
    pendingRouteIDs,
    apiErrors,
    routeByID,
    paID,
    onComplete,
    closeAction,
  ])

  const handle = social ? social.name : ""
  const emptyLabel = feeds.length
    ? "No feeds match that search."
    : noCandidatesLabel(connectedCount, loopCount)

  return (
    <div
      className="dlvr-modal suggest-feeds"
      role="dialog"
      aria-modal="true"
      aria-hidden="false"
    >
      <div className="tw-root">
        <div className="tw:flex tw:min-h-screen tw:w-full tw:items-start tw:justify-center tw:p-6 tw:sm:items-center">
          <NewSocialSuggestFeeds
            step={step}
            handle={handle}
            title={handle ? `Add ${handle} to your feeds` : "Add to your feeds"}
            description="Pick which feeds should also post to this account."
            avatarFallback={initials(handle)}
            avatar={
              social && social.icon ? (
                <img
                  src={social.icon}
                  alt=""
                  className="tw:size-full tw:object-cover"
                />
              ) : undefined
            }
            totalFeeds={feeds.length}
            recommendedLabel="MOST ACTIVE"
            recommended={recommended.map(toSuggestFeed)}
            allFeedsCount={rest.length}
            allFeeds={pageFeeds.map(toSuggestFeed)}
            emptyLabel={emptyLabel}
            page={currentPage}
            pages={pages}
            totalPages={totalPages}
            selectedIds={selectedIds}
            selectedFeeds={chosen.map(toSuggestFeed)}
            feedsExpanded={feedsExpanded}
            shareMostRecent={shareMostRecent}
            busy={saving}
            error={error}
            search={search}
            onSearchChange={(value) => {
              setSearch(value)
              setPage(1)
            }}
            onToggleFeed={toggleFeed}
            onToggleFeeds={toggleFeeds}
            onPageChange={setPage}
            onClose={closeAction}
            onSkip={closeAction}
            onContinue={() => setStep("confirm")}
            onCancel={() => setStep("browse")}
            onConfirm={confirm}
            onToggleFeedsExpanded={() => setFeedsExpanded((open) => !open)}
            onShareMostRecentChange={setShareMostRecent}
          />
        </div>
      </div>
    </div>
  )
}
